import { atomFamily, selector, selectorFamily } from "recoil";
import { layerDefs } from "./layerDefs";
import { popupAtom } from "./popupAtom";

export const layerDefsAtomFamily = atomFamily({
  key: "layers-family",
  default: (layerID) => layerDefs[layerID],
});

export const layerDefFormatSelector = selectorFamily({
  key: "layer-format-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layerDef = get(layerDefsAtomFamily(layerID));
      const filters = layerDef?.filters;
      if (!filters || !filters.filterByChildren) {
        return { source: layerDef.source, layer: layerDef.layer };
      }

      const { filterPropertyName, filterState } = layerDef.filters;
      const trueFilterValues = Object.keys(filterState).filter(
        (name) => filterState[name]
      );
      return {
        source: layerDef.source,
        layer: {
          ...layerDef.layer,
          paint: {
            ...layerDef.layer.paint,
            [`${layerDef.layer.type}-color`]: [
              "get",
              ["get", filterPropertyName],
              ["literal", layerDef.colours.colourState],
            ],
          },
          filter: [
            "in",
            ["get", filterPropertyName],
            ["literal", trueFilterValues],
          ],
        },
      };
    },
});

export const layerDefFilterStateSelector = selectorFamily({
  key: "layer-filters-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layer = get(layerDefsAtomFamily(layerID));
      const { filterState } = layer.filters;
      return filterState;
    },
  set:
    (layerID) =>
    ({ set }, newFilterState) => {
      if (layerID === "places") {
        set(layerDefsAtomFamily("places-boundary"), (oldLayer) => ({
          ...oldLayer,
          filters: { ...oldLayer.filters, filterState: newFilterState },
        }));
      }
      set(layerDefsAtomFamily(layerID), (oldLayer) => ({
        ...oldLayer,
        filters: { ...oldLayer.filters, filterState: newFilterState },
      }));
    },
});

export const layerDefColourStateSelector = selectorFamily({
  key: "layer-colour-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layer = get(layerDefsAtomFamily(layerID));
      return layer?.colours?.colourState;
    },
  set:
    (layerID) =>
    ({ set }, newColourState) => {
      set(layerDefsAtomFamily(layerID), (oldLayer) => ({
        ...oldLayer,
        colours: { ...oldLayer?.colours, colourState: newColourState },
      }));
    },
});

export const layerDefLabelsSelector = selectorFamily({
  key: "layer-labels-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layer = get(layerDefsAtomFamily(layerID));
      return layer.labels;
    },
});

export const layerDefFilterByChildrenSelector = selectorFamily({
  key: "layer-filter-by-children-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layer = get(layerDefsAtomFamily(layerID));
      return Boolean(layer?.filters?.filterByChildren);
    },
});

export const layerDefVisibilitySelector = selectorFamily({
  key: "layer-visibility-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layerDef = get(layerDefsAtomFamily(layerID));
      const visibility = layerDef.layer?.layout?.visibility;
      return visibility === "none" ? false : true;
    },
  set:
    (layerID) =>
    ({ set, get }, newVisibility) => {
      setLayerVisibility(newVisibility, set, layerID);
      if (layerID === "places") {
        setLayerVisibility(newVisibility, set, "places-boundary");
      }
    },
});

export const layerDefsIDsSelectorForCategory = selectorFamily({
  key: "layer-ids-selector",
  get:
    (category) =>
    ({ get }) => {
      // need to get those that are grouped and group them together e.g. need [id1, id2, [id3,id6], id7]
      let layerIDsForCategory = Object.keys(layerDefs).filter(
        (layerID) =>
          layerDefs[layerID].category === category &&
          layerID !== "places-boundary"
      );

      layerIDsForCategory.forEach((currentID) => {
        if (!layerDefs[currentID]?.grouping) {
          return;
        }
        const grouping = layerDefs[currentID].grouping;
        const groupingIDs = Object.keys(layerDefs).filter(
          (layerID) => layerDefs[layerID].grouping === grouping
        );
        const layerIDsInteresction = groupingIDs.filter((id) =>
          layerIDsForCategory.includes(id)
        );

        const index = layerIDsForCategory.indexOf(currentID);

        layerIDsForCategory[index] = layerIDsInteresction;
        layerIDsForCategory = layerIDsForCategory.filter(
          (id) => !layerIDsInteresction.includes(id)
        );

        return;
      });

      return layerIDsForCategory;
    },
});

export const layerDefsParentColourSelector = selectorFamily({
  key: "layer-parent-colour-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layerDef = get(layerDefsAtomFamily(layerID));
      if (layerDef?.filters?.filterByChildren) {
        return layerDef?.colours?.parentColour || "#00AEEF";
      }
      const layer = layerDef?.layer;
      const paint = layer?.paint;
      const type = layer?.type;

      return paint[`${type}-color`] ?? "#00AEEF";
    },
});

export const visibleLayerDefsIDsWithPopupSelector = selector({
  key: "visible-layer-ids-with-popup-selector",
  get: ({ get }) => {
    const layersWithPopup = Object.keys(layerDefs).filter(
      (layerID) => layerDefs[layerID]?.popupAttributes?.length > 0
    );
    const visibleLayersWithPopup = layersWithPopup.filter((layerID) => {
      const layerDef = get(layerDefsAtomFamily(layerID));
      const visibility = layerDef.layer?.layout?.visibility;
      return visibility === "none" ? false : true;
    });
    return visibleLayersWithPopup;
  },
});

export const layerDefPopupAttributesSelector = selectorFamily({
  key: "layer-popup-attributes-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layer = get(layerDefsAtomFamily(layerID));
      return layer?.popupAttributes;
    },
});

export const layerDefInfoSelector = selectorFamily({
  key: "layer-info-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const layer = get(layerDefsAtomFamily(layerID));
      return layer.info;
    },
});

export const layerDefOpacitySelector = selectorFamily({
  key: "layer-opacity-selector",
  get:
    (layerID) =>
    ({ get }) => {
      const { layer } = get(layerDefsAtomFamily(layerID));
      const { type } = layer;
      if (type === "fill" || type === "line") {
        return layer?.paint[`${type}-opacity`];
      }
      return;
    },
  set:
    (layerID) =>
    ({ set }, newOpacity) => {
      set(layerDefsAtomFamily(layerID), (oldLayer) => {
        const type = oldLayer.layer.type;
        if (type === "fill" || type === "line") {
          return {
            ...oldLayer,
            layer: {
              ...oldLayer.layer,
              paint: {
                ...oldLayer?.layer?.paint,
                [`${type}-opacity`]: newOpacity,
              },
            },
          };
        }
        return oldLayer;
      });
    },
});

export const layerDefGroupingSelector = selectorFamily({
  key: "layer-grouping",
  get:
    (layerID) =>
    ({ get }) => {
      const layer = get(layerDefsAtomFamily(layerID));
      return layer?.grouping;
    },
});

export const layerDefGroupVisibilitySelector = selectorFamily({
  key: "layer-group-visibility",
  get:
    (group) =>
    ({ get }) => {
      if (!group) {
        return null;
      }

      const groupLayerIDs = Object.keys(layerDefs).filter(
        (layerID) => layerDefs[layerID].grouping === group
      );

      const groupLayerVisibilities = groupLayerIDs.map((layerID) => {
        const layerDef = get(layerDefsAtomFamily(layerID));
        const visibility = layerDef.layer?.layout?.visibility;
        return visibility === "none" ? false : true;
      });

      if (groupLayerVisibilities.every((visibility) => visibility)) {
        return "all";
      }
      if (groupLayerVisibilities.every((visibility) => !visibility)) {
        return "none";
      }
      return "some";
    },
  set:
    (group) =>
    ({ set }, newVisibility) => {
      const groupLayerIDs = Object.keys(layerDefs).filter(
        (layerID) => layerDefs[layerID].grouping === group
      );
      groupLayerIDs.forEach((layerID) => {
        setLayerVisibility(newVisibility, set, layerID);
      });
    },
});
function setLayerVisibility(newVisibility, set, layerID) {
  if (!newVisibility) {
    // need to remove the popup if it is becoming invisible
    set(popupAtom, (oldPopup) => {
      if (oldPopup?.layerId === layerID) {
        return null;
      }
      return oldPopup;
    });
  }
  set(layerDefsAtomFamily(layerID), (oldLayer) => ({
    ...oldLayer,
    layer: {
      ...oldLayer.layer,
      layout: {
        ...oldLayer?.layer?.layout,
        visibility: newVisibility ? "visible" : "none",
      },
    },
  }));
}
